import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Users, Plus, ArrowLeft, Calendar, Trophy, Settings, Lightbulb, Trash2, Info } from 'lucide-react';
import { toast } from 'sonner';
import { useTheme } from '@/context/ThemeContext';

const API = `${window.location.origin}/api`;

// Suggested debate topics
const SUGGESTED_TOPICS = [
  "Was the Industrial Revolution more beneficial or harmful to society?",
  "Did the Roman Empire fall due to internal decay or external pressure?",
  "Was the French Revolution justified in its violent methods?",
  "Did colonialism have any positive effects on colonized nations?",
  "Was the atomic bombing of Japan necessary to end WWII?",
  "Did the Cold War prevent or cause more conflicts?",
  "Was the American Civil War primarily about slavery or states' rights?",
  "Did the Renaissance truly mark a break from the Middle Ages?"
];

const DebatePage = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [debates, setDebates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newTopic, setNewTopic] = useState('');
  const [userPosition, setUserPosition] = useState('for');
  const [creating, setCreating] = useState(false);
  const [selectedDebate, setSelectedDebate] = useState(null);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [feedbackData, setFeedbackData] = useState(null);
  const [loadingFeedback, setLoadingFeedback] = useState(false);

  useEffect(() => {
    loadDebates();
  }, []);

  const loadDebates = async () => {
    try {
      const response = await axios.get(`${API}/debate/list/all`, { withCredentials: true });
      setDebates(response.data);
    } catch (error) {
      toast.error('Failed to load debates');
    } finally {
      setLoading(false);
    }
  };

  const selectSuggestedTopic = (topic) => {
    setNewTopic(topic);
  };

  const createDebate = async () => {
    if (!newTopic.trim()) {
      toast.error('Please enter a topic');
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(
        `${API}/debate/start`,
        { 
          topic: newTopic,
          user_position: null  // Position will be selected in the debate room
        },
        { withCredentials: true }
      );
      setDialogOpen(false);
      setNewTopic('');
      // Reload debates list to show the new debate
      loadDebates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create debate');
    } finally {
      setCreating(false);
    }
  };

  const deleteDebate = async (debateId, e) => {
    e.stopPropagation(); // Prevent navigation when clicking delete
    e.preventDefault(); // Prevent default behavior
    
    if (!window.confirm('Are you sure you want to delete this debate? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${API}/debate/${debateId}`, { withCredentials: true });
      loadDebates(); // Reload the list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete debate');
    }
  };

  const showGradeFeedback = async (debate, e) => {
    e.stopPropagation(); // Prevent navigation when clicking score
    setSelectedDebate(debate);
    setFeedbackDialogOpen(true);
    
    // Always fetch detailed feedback with criteria breakdown
    setLoadingFeedback(true);
    try {
      const response = await axios.get(`${API}/debate/${debate.id}/feedback`, { withCredentials: true });
      setFeedbackData(response.data);
      
      // Update the debate in state with new score (for v2 format, overall_score will be undefined)
      // Only update if it's legacy format with overall_score
      if (response.data.overall_score !== undefined) {
        setDebates(prevDebates => 
          prevDebates.map(d => 
            d.id === debate.id 
              ? { ...d, argumentation_score: response.data.overall_score }
              : d
          )
        );
        
        // Also update selectedDebate
        setSelectedDebate(prev => ({ ...prev, argumentation_score: response.data.overall_score }));
      }
    } catch (error) {
      // Don't show error toast for 400 errors (expected for new debates with no arguments)
      if (error.response?.status !== 400) {
        toast.error('Failed to load feedback');
      }
      setFeedbackData(null);
    } finally {
      setLoadingFeedback(false);
    }
  };

  return (
    <div className="min-h-screen theme-bg">
      {/* Header */}
      <header className={`${isDarkMode ? 'bg-black/90 border-amber-900/50' : 'bg-white/80 border-amber-200'} backdrop-blur-sm border-b sticky top-0 z-50 shadow-sm`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/dashboard')}
                className={`${isDarkMode ? 'text-amber-400' : 'text-amber-700 hover:bg-amber-100'} btn-glow rounded-lg`}
                data-testid="back-to-dashboard-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center space-x-2">
                <Users className={`w-6 h-6 ${isDarkMode ? 'text-amber-500' : 'text-amber-700'}`} />
                <span className={`text-xl font-bold ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>Debate Room</span>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/profile')}
                className={`${isDarkMode ? 'text-amber-400' : 'text-amber-700 hover:bg-amber-100'} btn-glow rounded-lg`}
                title="Profile & Settings"
                data-testid="settings-btn"
              >
                <Settings className="w-5 h-5" />
              </Button>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-amber-600 hover:bg-amber-700" data-testid="new-debate-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    New Debate
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Start New Debate</DialogTitle>
                    <DialogDescription>
                      Choose a historical topic to debate
                    </DialogDescription>
                  </DialogHeader>
                  
                  <div className="space-y-6 py-4">
                    {/* Suggested Topics */}
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <Lightbulb className={`w-4 h-4 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`} />
                        <Label className={`text-sm font-semibold ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>Suggested Topics</Label>
                      </div>
                      <div className="grid gap-2 max-h-48 overflow-y-auto pr-2">
                        {SUGGESTED_TOPICS.map((topic, index) => (
                          <button
                            key={index}
                            onClick={() => selectSuggestedTopic(topic)}
                            className={`text-left p-3 rounded-lg border transition-all hover:bg-amber-50 hover:border-amber-300 ${
                              newTopic === topic 
                                ? 'bg-amber-50 border-amber-400 shadow-sm' 
                                : 'bg-white border-gray-200'
                            }`}
                            data-testid={`suggested-topic-${index}`}
                          >
                            <p className="text-sm text-amber-900">{topic}</p>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Custom Topic Input */}
                    <div className="space-y-2">
                      <Label htmlFor="topic">Or Enter Your Own Topic</Label>
                      <Input
                        id="topic"
                        placeholder="e.g., Was the Industrial Revolution beneficial?"
                        value={newTopic}
                        onChange={(e) => setNewTopic(e.target.value)}
                        data-testid="debate-topic-input"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end space-x-2">
                    <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button onClick={createDebate} disabled={creating || !newTopic.trim()} data-testid="start-debate-btn">
                      {creating ? 'Creating...' : 'Start Debate'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-12">
        <div className="mb-8">
          <h1 className={`text-4xl font-bold ${isDarkMode ? 'text-amber-100' : 'text-amber-900'} mb-4`}>Practice Your Argumentation Skills</h1>
          <p className={`text-lg ${isDarkMode ? 'text-amber-300' : 'text-amber-800'}`}>
            Engage in logical debates with Professor Clio on historical topics
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
          </div>
        ) : debates.length === 0 ? (
          <Card className="card-glow border-amber-200">
            <CardContent className="p-12 text-center">
              <Users className="w-16 h-16 text-amber-400 mx-auto mb-4" />
              <h3 className={`text-xl font-semibold mb-2 ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>No Debates Yet</h3>
              <p className={`mb-6 ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>Start your first debate to develop critical thinking and rhetorical skills</p>
              <Button 
                onClick={() => setDialogOpen(true)} 
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="first-debate-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Start First Debate
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {debates.map((debate) => (
              <Card
                key={debate.id}
                className="card-glow border-amber-200 cursor-pointer relative"
                onClick={() => navigate(`/debate/${debate.id}`)}
                data-testid={`debate-card-${debate.id}`}
              >
                <Button
                  variant="ghost"
                  size="icon"
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    deleteDebate(debate.id, e);
                  }}
                  className="absolute top-2 right-2 text-red-500 hover:text-red-700 hover:bg-red-50 min-w-[44px] min-h-[44px] z-10"
                  data-testid={`delete-debate-${debate.id}`}
                >
                  <Trash2 className="w-5 h-5" />
                </Button>
                <CardHeader>
                  <div className="flex items-start justify-between pr-10">
                    <div className="flex-1">
                      <CardTitle className={`${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>{debate.topic}</CardTitle>
                      <CardDescription className="flex items-center space-x-4 text-sm mt-2">
                        <span className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          {new Date(debate.created_at).toLocaleDateString()}
                        </span>
                        <button
                          onClick={(e) => showGradeFeedback(debate, e)}
                          className="flex items-center text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded transition-colors"
                          data-testid={`score-${debate.id}`}
                        >
                          <Trophy className="w-4 h-4 mr-1" />
                          View Grade
                          <Info className="w-3 h-3 ml-1 opacity-50" />
                        </button>
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-amber-700">
                      {debate.messages.length} messages
                    </span>
                    <span className={`text-sm font-medium ${
                      debate.completed ? 'text-green-600' : 'text-blue-600'
                    }`}>
                      {debate.completed ? 'Completed' : 'In Progress'}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Grade Feedback Dialog */}
      <Dialog open={feedbackDialogOpen} onOpenChange={setFeedbackDialogOpen}>
        <DialogContent className={`w-[95vw] max-w-2xl max-h-[80vh] overflow-y-auto overflow-x-hidden mx-auto ${isDarkMode ? 'shadow-[0_0_20px_rgba(251,146,60,0.7),0_0_40px_rgba(251,146,60,0.5),0_0_60px_rgba(251,146,60,0.3)]' : ''}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Trophy className="w-6 h-6 text-amber-600" />
              <span>Debate Grade {selectedDebate?.argumentation_score !== null ? 'Summary' : 'Feedback'}</span>
            </DialogTitle>
            <DialogDescription>
              Your argumentation analysis for: {selectedDebate?.topic}
            </DialogDescription>
          </DialogHeader>
          
          {loadingFeedback ? (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
              <p className="text-amber-700">Generating detailed feedback...</p>
            </div>
          ) : selectedDebate && (
            <div className="space-y-6 py-4">
              {/* New Qualitative Feedback Format */}
              {feedbackData && feedbackData.format_version === 'v2' ? (
                <div className="space-y-6">
                  {/* Professor's Reflection */}
                  {feedbackData.opponent_reflection && (
                    <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-200">
                      <div className="flex items-start space-x-3 mb-3">
                        <span className="text-3xl">🎭</span>
                        <h3 className="text-lg font-bold text-blue-900">Professor Clio&apos;s Reflection</h3>
                      </div>
                      <p className="text-blue-900 leading-relaxed italic">
                        &ldquo;{feedbackData.opponent_reflection}&rdquo;
                      </p>
                    </div>
                  )}

                  {/* Skill Assessment */}
                  {feedbackData.criteria && (
                    <div className="space-y-4">
                      <h3 className={`font-bold text-lg flex items-center ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>
                        <span className="text-2xl mr-2">📊</span>
                        Skill Assessment
                      </h3>
                      <div className="space-y-4">
                        {Object.entries(feedbackData.criteria).map(([criterion, data]) => {
                          const tierIcons = { developing: '🌱', proficient: '⚡', strong: '🌟', exceptional: '👑' };
                          const tierColors = { 
                            developing: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-900', bar: 'bg-blue-400' },
                            proficient: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-900', bar: 'bg-green-500' },
                            strong: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-900', bar: 'bg-purple-500' },
                            exceptional: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-900', bar: 'bg-yellow-500' }
                          };
                          const tierWidths = { developing: 'w-5/12', proficient: 'w-8/12', strong: 'w-11/12', exceptional: 'w-full' };
                          const tier = data?.tier || 'developing';
                          const color = tierColors[tier] || tierColors.developing;
                          
                          return (
                            <div key={criterion} className={`p-4 ${color.bg} rounded-lg border-2 ${color.border}`}>
                              <div className="mb-3">
                                <div className="flex items-center justify-between mb-2">
                                  <h4 className={`font-bold ${color.text} uppercase text-sm tracking-wide flex items-center`}>
                                    <span className="mr-2">
                                      {criterion === 'logic' && '🧠'}
                                      {criterion === 'evidence' && '📚'}
                                      {criterion === 'rhetoric' && '🎤'}
                                      {criterion === 'rebuttal' && '⚖️'}
                                    </span>
                                    {criterion === 'logic' && 'Logic & Reasoning'}
                                    {criterion === 'evidence' && 'Evidence & Facts'}
                                    {criterion === 'rhetoric' && 'Rhetoric & Clarity'}
                                    {criterion === 'rebuttal' && 'Counterarguments'}
                                  </h4>
                                  <span className={`text-xs font-bold ${color.text} uppercase px-3 py-1 rounded-full bg-white/50`}>
                                    {tierIcons[tier]} {tier}
                                  </span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                  <div className={`${color.bar} h-2 rounded-full ${tierWidths[tier]} transition-all duration-500`}></div>
                                </div>
                              </div>
                              <p className={`text-sm ${color.text} leading-relaxed`}>{data.feedback}</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Strengths & Growth */}
                  {(feedbackData.strengths || feedbackData.growth_opportunities) && (
                    <div className="space-y-4">
                      <h3 className={`font-bold text-lg flex items-center ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>
                        <span className="text-2xl mr-2">✨</span>
                        What Stood Out
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Strengths */}
                        {feedbackData.strengths && feedbackData.strengths.length > 0 && (
                          <div className="p-4 bg-green-50 rounded-lg border-2 border-green-200 overflow-hidden">
                            <h4 className="font-bold text-green-900 mb-3 uppercase text-sm">Strengths</h4>
                            <ul className="space-y-2">
                              {feedbackData.strengths.map((strength, idx) => (
                                <li key={idx} className="flex items-start text-sm text-green-900 min-w-0">
                                  <span className="mr-2 text-green-600 font-bold flex-shrink-0">✓</span>
                                  <span className="leading-relaxed break-words" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{strength}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {/* Growth Opportunities */}
                        {feedbackData.growth_opportunities && feedbackData.growth_opportunities.length > 0 && (
                          <div className="p-4 bg-amber-50 rounded-lg border-2 border-amber-200 overflow-hidden">
                            <h4 className="font-bold text-amber-900 mb-3 uppercase text-sm">Growth Opportunities</h4>
                            <ul className="space-y-2">
                              {feedbackData.growth_opportunities.map((growth, idx) => (
                                <li key={idx} className="flex items-start text-sm text-amber-900 min-w-0">
                                  <span className="mr-2 text-amber-600 font-bold flex-shrink-0">→</span>
                                  <span className="leading-relaxed break-words" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{growth}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Next Steps */}
                  {feedbackData.next_steps && (
                    <div className="p-6 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border-2 border-purple-200">
                      <h3 className="font-bold text-purple-900 mb-3 flex items-center text-lg">
                        <span className="text-2xl mr-2">🎯</span>
                        For Your Next Debate
                      </h3>
                      <p className="text-purple-900 leading-relaxed">
                        {feedbackData.next_steps}
                      </p>
                    </div>
                  )}
                </div>
              ) : feedbackData && feedbackData.overall_score !== undefined ? (
                /* Legacy score-based feedback */
                <div className="space-y-6">
                  <div className="flex items-center justify-between p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border-2 border-green-200">
                    <div>
                      <h3 className="text-sm font-medium text-gray-600 mb-1">
                        {selectedDebate.argumentation_score !== null ? 'Final Score' : 'Current Score'}
                      </h3>
                      <p className="text-4xl font-bold text-green-600">
                        {feedbackData.overall_score}/100
                      </p>
                    </div>
                    <div className="text-right">
                      <h3 className="text-sm font-medium text-gray-600 mb-1">Grade</h3>
                      <p className="text-2xl font-bold text-green-700">
                        {(() => {
                          const score = feedbackData.overall_score || 0;
                          return score >= 90 ? 'A' :
                                 score >= 80 ? 'B' :
                                 score >= 70 ? 'C' :
                                 score >= 60 ? 'D' : 'F';
                        })()}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <h3 className="font-semibold text-amber-900 flex items-center">
                      <span className="w-2 h-2 bg-amber-600 rounded-full mr-2"></span>
                      Performance Breakdown
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {feedbackData.criteria && Object.entries(feedbackData.criteria).map(([criterion, data]) => (
                        <div key={criterion} className="p-4 bg-white rounded-lg border border-amber-200">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-semibold text-amber-900 capitalize">{criterion}</h4>
                            <span className="text-lg font-bold text-green-600">{data.score}/25</span>
                          </div>
                          <p className="text-sm text-gray-700 leading-relaxed">{data.feedback}</p>
                        </div>
                      ))}
                    </div>
                    {feedbackData.summary && (
                      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <h4 className="font-semibold text-blue-900 mb-2">Overall Summary</h4>
                        <p className="text-sm text-blue-800">{feedbackData.summary}</p>
                      </div>
                    )}
                  </div>
                </div>
              ) : null}

              {/* Legacy Feedback Points - for completed debates */}
              {!feedbackData && selectedDebate.feedback && selectedDebate.feedback.length > 0 && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-amber-900 flex items-center">
                    <span className="w-2 h-2 bg-amber-600 rounded-full mr-2"></span>
                    Evaluation Breakdown
                  </h3>
                  <div className="space-y-3">
                    {selectedDebate.feedback.map((point, idx) => (
                      <div key={idx} className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                        <p className="text-amber-900 leading-relaxed">{point}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* No feedback available */}
              {!feedbackData && (!selectedDebate.feedback || selectedDebate.feedback.length === 0) && (
                <div className="p-6 bg-gray-50 rounded-lg border border-gray-200 text-center">
                  <p className="text-gray-600">
                    No detailed feedback available. Continue debating to build up your arguments.
                  </p>
                </div>
              )}

              {selectedDebate.argumentation_score === null && (
                <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <p className="text-sm text-yellow-800">
                    💡 This is feedback on your current performance. Complete the debate to receive your final score!
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end">
            <Button onClick={() => {
              setFeedbackDialogOpen(false);
              setFeedbackData(null);
            }}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DebatePage;
